import { useQuery, useMutation } from '@tanstack/react-query';
import { authService } from '@/services/AuthService';
import tokenStorage from '@adrodd/core/tokenStorage';
import { toast } from 'sonner';

// Query keys for auth
export const authKeys = {
  all: ['auth'] as const,
  currentUser: () => [...authKeys.all, 'currentUser'] as const,
};

/**
 * Get the current authenticated user.
 * Returns null if not authenticated (401/403). Throws on network errors so
 * that TanStack Query can retry and callers can surface a proper error state.
 */
export function useCurrentUser() {
  return useQuery({
    queryKey: authKeys.currentUser(),
    queryFn: () => authService.getCurrentUser(),
    // Cache for 30s so quick navigation doesn't hammer the API.
    staleTime: 30 * 1000,
    // Every route waits on this query, and the apiClient timeout is now 20s
    // (see apiClient.ts) — comfortable room above the measured p90 (7.24s),
    // so a first attempt only times out for a genuinely stuck request, not a
    // merely slow one. That makes a second full 20s attempt expensive relative
    // to its payoff: 2 retries could cost 3×20s+backoff before giving up,
    // which is worse for the one query gating first paint than accepting a
    // single retry (2 attempts total) for a transient blip.
    retry: 1,
    retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 8_000), // 2s
    // Only fetch if a token exists — avoids a noisy 401 for anonymous visitors.
    enabled: tokenStorage.hasTokens(),
    // Always re-check on mount to catch server-side session invalidation.
    refetchOnMount: true,
  });
}

/**
 * Request password reset instructions
 */
export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: ({ email, honeypot }: { email: string; honeypot?: string }) => authService.requestPasswordReset(email, honeypot),
    onSuccess: (response) => {
      toast.success(response?.message || 'If your account exists, we sent reset instructions.');
    },
    // No onError: ForgotPasswordPage's only caller shows the failure inline.
  });
}

/**
 * Reset password using token
 */
export function useResetPassword() {
  return useMutation({
    mutationFn: ({ token, password, honeypot }: { token: string; password: string; honeypot?: string }) =>
      authService.resetPassword(token, password, honeypot),
    // No onError: ResetPasswordPage's only caller shows the failure inline.
  });
}

/**
 * Accept a waitlist invitation and set password
 */
export function useAcceptInvite() {
  return useMutation({
    mutationFn: ({ email, inviteToken, password, name }: { email: string; inviteToken: string; password: string; name?: string }) =>
      authService.acceptInvite(email, inviteToken, password, name),
    // No onError: ResetPasswordPage's invite flow shows the failure inline.
  });
}
